import { SEO } from '@/components/SEO';

export default function PrivacyPolicy() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <SEO 
        title="Privacy Policy - QuickFreeInvoice" 
        description="Read the Privacy Policy for QuickFreeInvoice. Learn how we handle your personal data, client profiles, and invoice information securely and confidentially." 
        keywords="invoice privacy policy, billing security, data privacy"
      />
      <h1 className="font-heading text-4xl font-bold mb-8">Privacy Policy</h1>
      
      <div className="prose prose-lg max-w-none space-y-8">
        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-muted-foreground leading-relaxed">
            quickfreeinvoicetool.com ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our invoice generation service.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">2. Data We Collect</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            We collect information that you provide directly to us, including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Account information: email address, name, and password</li>
            <li>Business information: company name, address, tax number, and contact details</li>
            <li>Invoice data: customer information, line items, and payment details you create</li>
            <li>Payment information: processed securely through Stripe (we do not store credit card data)</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            We do not use tracking technologies or third-party analytics that track your behavior across other websites.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">3. How We Use Your Data</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            We use the information we collect to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Provide, maintain, and improve our invoice creation service</li>
            <li>Process your transactions and send you related information</li>
            <li>Send you technical notices, updates, and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Detect, prevent, and address technical issues and fraudulent activity</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            We do not sell, rent, or share your personal information with third parties for their marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
          <p className="text-muted-foreground leading-relaxed">
            Your data is stored securely in Firebase (Google Cloud Platform) servers with industry-standard encryption. 
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">5. Cookies</h2>
          <p className="text-muted-foreground leading-relaxed">
            We use only essential session cookies required for authentication and service functionality. 
            We do not use advertising cookies or third-party tracking cookies.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">6. Third-Party Services</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            We use the following third-party services:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li><strong>Firebase (Google):</strong> For authentication, database, and file storage. 
            <a href="https://firebase.google.com/support/privacy" className="text-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">
              Firebase Privacy Policy
            </a>
            </li>
            <li><strong>Stripe:</strong> For payment processing. 
            <a href="https://stripe.com/privacy" className="text-primary hover:underline ml-1" target="_blank" rel="noopener noreferrer">
              Stripe Privacy Policy
            </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">7. Your Rights</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            You have the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Access, update, or delete your personal information at any time through your Profile page</li>
            <li>Export your invoice data</li>
            <li>Delete your account and all associated data permanently</li>
            <li>Opt out of promotional communications (if any)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">8. Data Deletion</h2>
          <p className="text-muted-foreground leading-relaxed">
            When you delete your account from the Profile page, all your data is permanently deleted, including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mt-3">
            <li>All invoices and business profiles</li>
            <li>Uploaded logos and files</li>
            <li>Account information and authentication data</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-3">
            This action is irreversible and cannot be undone.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">9. Children's Privacy</h2>
          <p className="text-muted-foreground leading-relaxed">
            Our service is not intended for users under the age of 18. We do not knowingly collect 
            information from children under 18.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">10. Changes to This Policy</h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last Updated" date below. 
            For material changes, we will send you an email notification.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">11. Contact Us</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            <strong>Email:</strong>{' '}
            <a href="mailto:privacy@quickfreeinvoicetool.com" className="text-primary hover:underline">
              privacy@quickfreeinvoicetool.com
            </a>
          </p>
        </section>

        <div className="border-t pt-8 mt-12">
          <p className="text-sm text-muted-foreground">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
